/**
 * Gets the corresponding hex code value for a given color and shade of brand colors.
 */
export function getBrandColorHex(color: keyof BrandColorVariants, shade: keyof BrandColorShades) {
  return brandColors[color][shade];
}

/**
 * Gets the corresponding hex code value for a given color and shade of brand colors, set to a specific opacity.
 */
export function getTransparentBrandColorHex(
  color: keyof BrandColorVariants,
  shade: keyof BrandColorShades,
  opacity: number,
) {
  const opacityHex = Math.floor(255 * opacity).toString(16);
  return `${getBrandColorHex(color, shade)}${opacityHex}`;
}

export type BrandColorToken = `new.${keyof BrandColorVariants}.${keyof BrandColorShades}`;

export type BrandColorVariants = {
  red: BrandColorShades;
  yellow: BrandColorShades;
  green: BrandColorShades;
  blue: BrandColorShades;
  violet: BrandColorShades;
  gray: BrandColorShades;
};

export type BrandColorShades = {
  50: string;
  150: string;
  300: string;
  500: string;
  700: string;
  900: string;
  950: string;
};

// Brand colors from: https://www.figma.com/design/o0nXlmHB4Zqhu1i0j8BXpP/Hightouch-Design-System?m=dev&node-id=3673%3A4350
const brandColors: BrandColorVariants = {
  red: {
    "50": "#FFF4F4",
    "150": "#FEE4E3",
    "300": "#FEB1B5",
    "500": "#F85D3E",
    "700": "#DE2D22",
    "900": "#76230E",
    "950": "#2B0205",
  },
  yellow: {
    "50": "#FFF6E4",
    "150": "#FEE6CA",
    "300": "#F9BA3E",
    "500": "#DB752C",
    "700": "#BA5821",
    "900": "#65320E",
    "950": "#240802",
  },
  green: {
    "50": "#E3FCEE",
    "150": "#B2F9D4",
    "300": "#40DE9E",
    "500": "#2EA281",
    "700": "#228457",
    "900": "#0F492E",
    "950": "#021504",
  },
  blue: {
    "50": "#EBF9FC",
    "150": "#CCF0F9",
    "300": "#8BCDF0",
    "500": "#3498DA",
    "700": "#2D71DC",
    "900": "#153E7E",
    "950": "#02131B",
  },
  violet: {
    "50": "#F9F5FF",
    "150": "#EAE7FE",
    "300": "#C6BFF1",
    "500": "#9280F4",
    "700": "#765BF0",
    "900": "#421BB0",
    "950": "#12043E",
  },
  gray: {
    "50": "#F3F7F5",
    "150": "#E4ECE7",
    "300": "#BDC7C2",
    "500": "#88938D",
    "700": "#6D7772",
    "900": "#3B413E",
    "950": "#101111",
  },
};

const colors = {
  new: {
    ...brandColors,
  },
  // Product colors from: https://www.figma.com/design/rLsSTygVap7zagoErmncwm/Styles?m=dev&node-id=1096%3A44996
  product: {
    teal: {
      "50": "oklch(0.98 0.0174 209.43)",
      "100": "oklch(0.9637 0.0326 212.04)",
      "200": "oklch(0.9125 0.0507 210.29)",
      "300": "oklch(0.8419 0.0658 213.26)",
      "400": "oklch(0.7487 0.1036 211.39)",
      "500": "oklch(0.5972 0.131 213.44)",
      "600": "oklch(0.5243 0.1143 214.28)",
      "700": "oklch(0.4668 0.0943 213.73)",
      "800": "oklch(0.4025 0.0826 212)",
      "900": "oklch(0.3427 0.071 210.27)",
    },
    yellow: {
      "50": "oklch(0.9871 0.0239 94.52)",
      "100": "oklch(0.9639 0.0686 97)",
      "200": "oklch(0.9249 0.1183 97.1)",
      "300": "oklch(0.8788 0.148 92.73)",
      "400": "oklch(0.8266 0.1731 86.1)",
      "500": "oklch(0.7659 0.1668 71.73)",
      "600": "oklch(0.6392 0.1581 60.07)",
      "700": "oklch(0.548 0.148 50.76)",
      "800": "oklch(0.4672 0.1315 48.45)",
      "900": "oklch(0.3748 0.1115 47.49)",
    },
    red: {
      "50": "oklch(96.91% 0.015 17.42)",
      "100": "oklch(90.23% 0.050764487400613734 18.134245085608146)",
      "200": "oklch(83.48% 0.09155694161436399 18.345986095067442)",
      "300": "oklch(76.76% 0.1379562554882739 18.596481624165502)",
      "400": "oklch(70.13% 0.19088395210545106 18.65430749379855)",
      "500": "oklch(63.33% 0.213 18.7)",
      "600": "oklch(54.91% 0.22 18.59)",
      "700": "oklch(43.46% 0.174 18.79)",
      "800": "oklch(31.64% 0.12686925279130523 18.9842584108985)",
      "900": "oklch(21.08% 0.08451422681622453 18.772611548125568)",
    },
    gray: {
      "50": "oklch(98.51% 0 355)",
      "100": "oklch(97.02% 0 355)",
      "200": "oklch(94.01% 0 0)",
      "300": "oklch(90.24% 0.0025 345.22)",
      "400": "oklch(81.84% 0.0057 3.37)",
      "500": "oklch(63.8% 0.0061 3.44)",
      "600": "oklch(50.59% 0.0082 355.05)",
      "700": "oklch(40.77% 0.0031 345.34)",
      "800": "oklch(30.18% 0.004 355)",
      "900": "oklch(23.34% 0.004 355)",
    },
    green: {
      "50": "oklch(0.96 0.0148 158.25)",
      "100": "oklch(0.9298 0.0478 156.56)",
      "200": "oklch(0.856 0.0977 154.31)",
      "300": "oklch(0.7761 0.1295 152.9)",
      "400": "oklch(0.6717 0.1422 153.17)",
      "500": "oklch(0.5904 0.1616 152.22)",
      "600": "oklch(0.4551 0.1329 155.25)",
      "700": "oklch(0.3832 0.1031 156.41)",
      "800": "oklch(0.3277 0.0924 157.43)",
      "900": "oklch(0.3034 0.0685 169.68)",
    },
    blue: {
      "50": "oklch(0.9709 0.0125 255.5)",
      "100": "oklch(0.9338 0.0348 259.59)",
      "200": "oklch(0.883 0.0625 259.03)",
      "300": "oklch(0.8115 0.0996 256.56)",
      "400": "oklch(0.707 0.158 259.16)",
      "500": "oklch(0.6199 0.2108 263.3)",
      "600": "oklch(0.5178 0.2286 265.2)",
      "700": "oklch(0.4803 0.2313 266.03)",
      "800": "oklch(0.4043 0.2004 267.27)",
      "900": "oklch(0.321 0.1378 267.64)",
    },
    purple: {
      "50": "oklch(0.9575 0.0114 308.33)",
      "100": "oklch(0.8997 0.0245 306.94)",
      "200": "oklch(0.8076 0.0506 307.77)",
      "300": "oklch(0.727 0.0731 307.34)",
      "400": "oklch(0.6511 0.0936 307.59)",
      "500": "oklch(0.5972 0.1079 307.75)",
      "600": "oklch(0.524 0.1061 307.91)",
      "700": "oklch(0.4362 0.0889 307.9)",
      "800": "oklch(0.3393 0.0694 308.56)",
      "900": "oklch(0.2977 0.0622 308.08)",
    },
    pink: {
      "50": "oklch(97.18% 0.011 339.33)",
      "100": "oklch(94.76% 0.023 338.62)",
      "200": "oklch(89.55% 0.051 340.45)",
      "300": "oklch(81.57% 0.1 343.67)",
      "400": "oklch(70.56% 0.171 347.84)",
      "500": "oklch(64.07% 0.205 352.56)",
      "600": "oklch(57.55% 0.214 359.48)",
      "700": "oklch(51.08% 0.191 2.82)",
      "800": "oklch(44.62% 0.16 2.77)",
      "900": "oklch(39.84% 0.13 1.02)",
    },
    orange: {
      "50": "oklch(98.1% 0.015 80.71)",
      "100": "oklch(95.42% 0.034 80.31)",
      "200": "oklch(89.96% 0.083 73.51)",
      "300": "oklch(83.26% 0.134 68.84)",
      "400": "oklch(74.21% 0.173 57.75)",
      "500": "oklch(69.47% 0.196 49.2)",
      "600": "oklch(63.37% 0.196 42.09)",
      "700": "oklch(54.13% 0.178 39.62)",
      "800": "oklch(46.06% 0.155 38.44)",
      "900": "oklch(36.7% 0.123 39.51)",
    },
    indigo: {
      "50": "oklch(94.96% 0.023 291.37)",
      "100": "oklch(91.63% 0.042 291.47)",
      "200": "oklch(84.1% 0.075 290.63)",
      "300": "oklch(78.42% 0.091 290.6)",
      "400": "oklch(67.89% 0.122 289.83)",
      "500": "oklch(57.6% 0.135 289.1)",
      "600": "oklch(46.86% 0.135 287.37)",
      "700": "oklch(38.9% 0.124 288.18)",
      "800": "oklch(31.66% 0.099 289.01)",
      "900": "oklch(20.7% 0.108 289.18)",
    },
  },
};

const themeColors = {
  ...colors,
  brand: {
    text: {
      light: colors.new.gray["950"],
      dark: colors.new.gray["50"],
    },
    textSecondary: {
      light: colors.new.gray["900"],
      dark: colors.new.gray["150"],
    },
    // Tertiary is in-between primary and secondary shades.
    // Often used to meet a11y compliance when secondary is too low-contrast.
    textTertiary: {
      light: colors.new.gray["900"],
      dark: colors.new.gray["150"],
    },
    background: {
      light: "white",
      dark: colors.new.gray["950"],
    },
    backgroundSecondary: {
      light: colors.new.gray["50"],
      dark: colors.new.gray["900"],
    },
    border: {
      extraLight: colors.new.gray["150"],
      light: colors.new.gray["300"],
      dark: colors.new.gray["500"],
    },
    header: {
      border: {
        light: "rgba(59, 65, 62, 0.17)",
        dark: "rgba(255, 255, 255, 0.1)",
      },
      backgroundSecondary: {
        light: "rgba(255, 255, 255, 0.60)",
        dark: "rgba(255, 255, 255, 0.06)",
      },
      backgroundTertiary: {
        light: "rgba(255, 255, 255, 0.30)",
        dark: "rgba(255, 255, 255, 0.12)",
      },
    },
    accent: {
      primary: colors.new.green["300"],
      secondary: colors.new.green["500"],
      compliment: colors.new.gray["950"],
    },
    logo: {
      light: colors.new.gray["950"],
      dark: "#ffffff",
    },
    black: "#000000",
    white: "#ffffff",
    error: colors.new.red["700"],
    success: colors.new.green["500"],
  },
  tools: {
    snowflake: "#29B5E8",
    databricks: "#ED3C2C",
    bigquery: "#669DF6",
    redshift: "#0096fa",
    generic: colors.new.green["300"],
  },
  // Customers
  wmg: "#0032A0",
  cars: "#65299D",
  chime: "#1EC677",
  intelycare: "#E5098C",
  ramp: "#787868",
  lucidchart: "#282c33",
  gorgias: "#fc8165",
  chalhoub: "#618eb4",
  wellthy: "#E12723",
  mews: "#ff5e64",
  prestashop: "#a4dbe8",
  circleci: "#049b4a",
  retool: "#6875d9",
  vendr: "#5e2df5",
  blend: "#00146b",
  nandos: "#eb3f56",
  zeplin: "#ee6723",
  gosite: "#0058ff",
  seesaw: "#1b70c0",
  "auto-trader": "#222B5E",
  "on-deck": "#315be8",
  "compare-club": "#0ac16a",
  "the-zebra": "#574cfa",
  petsmart: "#cd303b",
  "weight-watchers": "#3C34E5",
};

export const intercomLauncherColor = themeColors.brand.backgroundSecondary.dark;

export default themeColors;

export type Colors = typeof themeColors;
